// AddEditNotification.js - Main Wizard Component
import React, { createContext, useState, useEffect } from 'react';
import { Box, Card, Stepper, Step, StepLabel,CardContent } from '@mui/material';
import {  People, FilterAlt, Schedule, Description, Send } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Swal from 'sweetalert2';
import { NotificationServices } from './NotificationServices';
import { LocalStore } from '../../../storage/authStore';

// Step components
import FilterSection from './Steps/FilterSection';
import ScheduleAndMode from './Steps/ScheduleAndMode';
import ReviewAndSend from './Steps/ReviewAndSend';
import AudienceSelection from './Steps/AudienceSelection';
import NotificationContent from './Steps/NotificationContent';

const NotificationDataContext = createContext();

const AddEditNotification = ({setTabValue}) => {
    
    const initialState = {
        // Step 1: Audience Selection
        audienceType: '',
        
        // Step 2: Filters
        customerFilters: {
            selectedCustomers: [],
            locationFilters: {
                        locationFilter: 'radius',
                        centerCoordinates: { lat: '', lng: '' },
                        radius: '',
                        polygon: []
                    },
            filterOptions:[]
        },
        expertFilters: {
            selectedExperts: [],
            locationFilters: {
                        locationFilter: 'radius',
                        centerCoordinates: { lat: '', lng: '' },
                        radius: '',
                        polygon: []
                    },
            filterOptions:[]
        },
        
        // CSV Upload for non-registered users
        csvUploadData: {
            phoneNumbers: [], // Array of phone numbers from CSV
            fileName: ''
        },
        
        // Step 3: Schedule & Mode
        scheduleType: 'now',
        scheduledDateTime: null,
        deliveryModes: [],
        
        // Step 4: Content
        title: '',
        body: '',
        
        // WhatsApp specific
        whatsappData:{
            
        }
    };
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [notificationData, setNotificationData] = useState(initialState);
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        { label: 'Select Audience', icon: <People /> },
        { label: 'Apply Filters', icon: <FilterAlt /> },
        { label: 'Schedule & Mode', icon: <Schedule /> },
        { label: 'Content', icon: <Description /> },
        { label: 'Review & Send', icon: <Send /> }
    ];

    const NotificationFormMethod = useForm({
        mode: 'onChange',
        defaultValues: notificationData
    });

    const { reset } = NotificationFormMethod;

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const handleFormSubmit = async () => {
        console.log("Submitting Notification Data:", notificationData);
        
        let selectedAudience = [];
        let convertIdObjectsToStrings = [];
        
        // Handle CSV upload audience type
        if (notificationData.audienceType === 'csvUpload') {
            selectedAudience = notificationData.csvUploadData?.phoneNumbers || [];
        } else {
            selectedAudience = notificationData.audienceType === 'customer' 
                ? notificationData.customerFilters.selectedCustomers 
                : notificationData.expertFilters.selectedExperts;
            convertIdObjectsToStrings = selectedAudience.map(item => String(item.id));
        }
        
        const formatData = { 
            customerIds: notificationData.audienceType === 'customer' ? convertIdObjectsToStrings : [], 
            expertIds: notificationData.audienceType === 'expert' ? convertIdObjectsToStrings : [], 
            phoneNumbers: notificationData.audienceType === 'csvUpload' ? selectedAudience : [], // CSV phone numbers
            title: notificationData.title, 
            body: notificationData.body, 
            type: 'campaign', 
            event: 'custom', 
            audienceType: notificationData.audienceType, 
            scheduleType: notificationData.scheduleType, 
            scheduledDateTime: notificationData.scheduledDateTime, 
            deliveryModes: notificationData.deliveryModes, 
            senderUserId: LocalStore.getCurrentUserDetails().id, 
            filters: notificationData.audienceType === 'customer' ? notificationData.customerFilters : notificationData.expertFilters
        };
        
        // Add WhatsApp data if WhatsApp is selected
        if (notificationData.deliveryModes.includes('whatsapp') && notificationData.whatsappData) {
            const whatsappData = notificationData.whatsappData;
            let whatsappFormatted = {};
            if (whatsappData.messageType === 'template') {
                whatsappFormatted.messageType = 'template';
                whatsappFormatted.templateName = whatsappData.templateName;
                whatsappFormatted.languageCode = whatsappData.languageCode;
                whatsappFormatted.components = whatsappData.components;
                // whatsappFormatted.whatsappTemplateParams = whatsappData.whatsappTemplateParams;
            } else if (whatsappData.messageType === 'text') {
                whatsappFormatted.messageType = 'text';
                whatsappFormatted.message = whatsappData.message;
            } else if (whatsappData.messageType === 'media') {
                whatsappFormatted.messageType = 'media';
                whatsappFormatted.mediaType = whatsappData.mediaType;
                whatsappFormatted.mediaUrl = whatsappData.mediaUrl;
                whatsappFormatted.caption = whatsappData.caption;
            }
            formatData.whatsappData = whatsappFormatted;
        }
        
        console.log("formatted Data:", formatData); 
        // return;
        
        const audienceTypeLabel = notificationData.audienceType === 'csvUpload' 
            ? 'phone number(s)' 
            : `${notificationData.audienceType}(s)`;
        
        const result = await Swal.fire({ 
            title: 'Are you sure?', 
            text: `You want to send notification to ${selectedAudience.length} ${audienceTypeLabel}?`, 
            icon: 'warning', 
            showCancelButton: true, 
            confirmButtonText: 'Yes', 
            cancelButtonText: 'No', 
            allowOutsideClick: false,  
        }); 
        if (result.isConfirmed) { 
            Swal.fire({ 
                title: 'Processing...', 
                text: `Please wait while we ${notificationData.scheduleType === 'now' ? 'send' : 'schedule'} notification`, 
                allowOutsideClick: false, 
                didOpen: () => { 
                    Swal.showLoading(); 
                }, 
            }); 
            
            
            try { 
            const createResponse = await NotificationServices.sendNotification(formatData); 
            if (createResponse.success == "1") { 
            await Swal.fire({ 
                title: 'Success', 
                text: `Notification ${notificationData.scheduleType === 'now' ? 'sent' : 'scheduled'} successfully`, 
                icon: 'success', 
                confirmButtonText: 'OK', 
                timer: 1600, 
            }); 
            reset(initialState); 
            setNotificationData(initialState); 
            setActiveStep(0); 
            // navigate('/notifications'); 
            //Navigate back to notifications list 
            setTabValue('1');
            } else { 
                await Swal.fire({ 
                title: 'Failed', 
                text: createResponse.message || 'Notification operation failed', 
                icon: 'error', 
                confirmButtonText: 'OK', 
                }); 
            } 
            } catch (error) { 
                console.error('Error submitting notification:', error); 
                await Swal.fire({ 
                    title: 'Error', 
                    text: 'An unexpected error occurred', 
                    icon: 'error', 
                    confirmButtonText: 'OK', 
                }); 
            } 
        }
    };

    const ColorStepIcon = (props) => {
    const { active, completed, icon } = props;

    return (
        <Box
        sx={{
            borderRadius: "50%",
            p:'20%',
            display: "flex",
            alignItems: "center",
            bgcolor: completed
            ? "success.main"   // green if completed
            : active
            ? "primary.main"   // primary if active
            : "text.secondary", // grey otherwise
            color: "#fff"   // grey otherwise
        }}
        >
        {React.cloneElement(icon, {
            fontSize: "small"
        })}
        </Box>
    );
    };
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <NotificationDataContext.Provider value={{ notificationData,  setNotificationData,steps, activeStep, setActiveStep , initialState ,selectedTemplate, setSelectedTemplate,completedSteps, setCompletedSteps,handleBack}}>
                <FormProvider {...NotificationFormMethod}></FormProvider>
                <Box>
                    {/* Stepper */}
                    <Card sx={{ mb: 1 }}>
                        <CardContent sx={{pb:'.5rem !important'}}>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((step, index) => (
                                    <Step key={step.label} completed={completedSteps.has(index)}>
                                        <StepLabel 
                                            StepIconComponent={(props) => (
                                                <ColorStepIcon {...props} icon={step.icon} />
                                            )}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    mt:'2%',
                                                    fontSize: '0.8rem',
                                                    fontWeight: activeStep === index  ? 600 : 400,
                                                    color: activeStep === index
                                                            ? "primary.main" // active step
                                                            : completedSteps.has(index)
                                                            ? "success.main" // completed step
                                                            : "text.secondary",
                                                }
                                            }}
                                        >
                                            {step.label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </CardContent>
                    </Card>
                    <Box sx={{overflowY: 'auto' }}>
                        {/* Step Content */}
                        {activeStep == '0' && <AudienceSelection />}
                        {activeStep == '1' && <FilterSection />}
                        {activeStep == '2' && <ScheduleAndMode />}
                        {activeStep == '3' && <NotificationContent />}
                        {activeStep == '4' && <ReviewAndSend onSubmit={handleFormSubmit} />}
                    </Box>
                </Box>
            </NotificationDataContext.Provider>
        </LocalizationProvider>
    );
};

export default AddEditNotification;
export { NotificationDataContext };
